/**
 * FASE 20.3 — PERMISSION FILTER LAYER
 *
 * Role-based data scoping injected into every adapter query.
 *
 * Roles:
 *  coordinator       → scoped to own church_id only
 *  district_pastor   → scoped to district_id (all churches in district)
 *  admin             → district-scoped (full district access)
 *  *                 → same as district_pastor (safe default)
 */

/**
 * @typedef {Object} PermissionScope
 * @property {'church'|'district'|'unrestricted'} level
 * @property {string|null} district_id
 * @property {string|null} church_id
 * @property {string}      role
 */

/**
 * Derive the effective permission scope from appScope.
 * Called once per tool execution — pure, no side effects.
 *
 * @param {{ role?: string, district_id?: string, church_id?: string }} appScope
 * @returns {PermissionScope}
 */
export function resolvePermissionScope(appScope = {}) {
  const role        = appScope?.role        ?? "*";
  const district_id = appScope?.district_id ?? null;
  const church_id   = appScope?.church_id   ?? null;

  if (role === "coordinator") {
    // Coordinators can only see their own church
    return { level: "church", district_id, church_id, role };
  }

  if (role === "admin" || role === "district_pastor") {
    // District-level access — all churches in their district
    return { level: "district", district_id, church_id: null, role };
  }

  // Default: district-scoped (safe fallback)
  return { level: "district", district_id, church_id: null, role };
}

/**
 * Apply permission scope to a Supabase query builder.
 * Call this on every query before executing.
 *
 * @param {Object} query           — Supabase query builder
 * @param {PermissionScope} scope
 * @returns {Object}               — scoped query builder
 */
export function applyPermissionScope(query, scope) {
  if (scope.level === "church" && scope.church_id) {
    return query.eq("church_id", scope.church_id);
  }
  if (scope.level === "district" && scope.district_id) {
    return query.eq("district_id", scope.district_id);
  }
  // No valid scope → return a query that will yield zero rows safely
  // Rather than no-op, we apply an impossible filter so no data leaks
  return query.eq("id", "00000000-0000-0000-0000-000000000000");
}

/**
 * Check if a role is allowed to execute a given tool schema.
 * @param {import('./toolSchemas').DataToolSchema} schema
 * @param {string} role
 * @returns {boolean}
 */
export function isRoleAllowed(schema, role) {
  if (!schema) return false;
  if (schema.requiredRole.includes("*")) return true;
  return schema.requiredRole.includes(role);
}
